import { useState, useMemo } from "react";
import { ArrowLeft, Edit2, Pause, Play, X, Trash2 } from "lucide-react";
import Button from "../ui/Button";
import Pill from "../ui/Pill";
import Card from "../ui/Card";
import { Table, THead, TR, TH, TD, EmptyState } from "../ui/Table";
import ConfirmDialog from "../ui/ConfirmDialog";
import Modal from "../ui/Modal";
import Input from "../ui/Input";
import InlinePicker from "../ui/InlinePicker";
import AddClientForm from "./AddClientForm";
import PaymentMatrix from "./PaymentMatrix";
import DealsSection from "./DealsSection";
import { formatUSD, formatMonths } from "../../lib/formatters";
import { formatDate, toISO, today } from "../../lib/dateUtils";
import {
  calculateClientLTV, calculateClientMonthsActive, calculateOutstandingAR,
  getClientsByEmail, calculateCombinedLTVByEmail,
} from "../../lib/calculations";
export default function ClientProfile({ clientId, state, dashboard, ghl, onBack }) {
  const { clients, offers, settings, payments } = state;
  const client = clients.find((c) => c.id === clientId);

  const [editOpen, setEditOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [ending, setEnding] = useState(null); // { targetStatus, date }

  if (!client) {
    return (
      <div>
        <Button variant="ghost" onClick={onBack}><ArrowLeft size={14} /> Back</Button>
        <EmptyState title="Client not found" />
      </div>
    );
  }

  const offer = offers.find((o) => o.id === client.primaryOfferId);
  const clientPayments = payments.filter((p) => p.clientId === client.id);
  const totalPaid = calculateClientLTV(client.id, payments);
  const months = calculateClientMonthsActive(client);
  const _isClientPaying = client.status === "active" || client.status === "paid_pause";
  const _clientRate = Number(client.monthlyRate) || 0;
  const mrr = client.primaryType === "recurring" && _isClientPaying ? _clientRate : 0;
  const outstanding = calculateOutstandingAR(clientPayments);
  const paidCount = clientPayments.filter((p) => p.status === "paid").length;

  // "Remaining" = price billed against the client minus what's been collected,
  // regardless of whether a pending payment record exists for the gap.
  //   recurring: monthsActive × monthlyRate (expected lifetime billed so far)
  //   oneoff:    monthlyRate (the deal value)
  // Clamped at 0 so overpayment doesn't show negative.
  const totalBilled = client.primaryType === "recurring"
    ? Math.max(1, months) * _clientRate
    : _clientRate;
  const remaining = Math.max(0, totalBilled - totalPaid);

  const relatedEngagements = client.email
    ? getClientsByEmail(client.email, clients).filter((c) => c.id !== client.id)
    : [];
  const combinedLTV = client.email
    ? calculateCombinedLTVByEmail(client.email, clients, payments)
    : totalPaid;
  const hasMultipleEngagements = relatedEngagements.length > 0;

  return (
    <div className="space-y-5">
      <Button variant="ghost" onClick={onBack}><ArrowLeft size={14} /> Back to clients</Button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display font-semibold text-2xl text-brand-ink dark:text-[var(--text-primary)]">{client.name}</h1>
            <Pill status={client.status} />
            <Pill status={client.primaryType === "recurring" ? "recurring" : "oneoff"}>{client.primaryType}</Pill>
          </div>
          <div className="text-sm text-brand-slate mt-1">
            {client.country} · {client.type} · {offer?.name || "—"} · started {formatDate(client.startDate)}
            {client.churnDate && ` · churned ${formatDate(client.churnDate)}`}
          </div>
          <div className="text-xs text-brand-slate mt-1">
            {client.email ? <span className="text-brand-ink dark:text-[var(--text-primary)] font-medium">{client.email}</span> : "No email"}
            {Array.isArray(client.aliasEmails) && client.aliasEmails.length > 0 && (
              <span className="ml-1.5 inline-flex items-center gap-1">
                {client.aliasEmails.map((ae) => (
                  <span
                    key={ae}
                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-200"
                    title="Alias email (from merged client)"
                  >
                    also {ae}
                  </span>
                ))}
              </span>
            )}
            {" · "}Source: {client.acquisitionSource || "—"}
            {client.funnel ? ` · Funnel: ${client.funnel}` : ""}
          </div>
          {client.notes && <div className="mt-2 text-sm text-brand-slate max-w-2xl">{client.notes}</div>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" onClick={() => setEditOpen(true)}><Edit2 size={14} /> Edit</Button>
          <div className="px-3 py-2 rounded-lg bg-gray-50 dark:bg-[var(--bg-soft)] border border-gray-200 dark:border-[var(--border-soft)] flex items-center gap-2">
            <span className="text-xs text-brand-slate">Status:</span>
            <select
              value={client.status === "paused" ? "free_pause" : client.status === "churned" ? "cancelled" : client.status}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "cancelled" || v === "completed") {
                  setEnding({ targetStatus: v, date: client.churnDate || toISO(today()) });
                } else {
                  dashboard.updateClient(client.id, { status: v });
                }
              }}
              className="text-sm font-medium bg-transparent focus:outline-none cursor-pointer"
            >
              <option value="active">Active</option>
              <option value="free_pause">Free pause</option>
              <option value="paid_pause">Paid pause</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <Button variant="danger" onClick={() => setConfirm({
            title: "Delete client",
            message: `Delete ${client.name} and all their payments? This cannot be undone.`,
            confirmLabel: "Delete",
            onConfirm: () => { dashboard.deleteClient(client.id); onBack(); },
          })}><Trash2 size={14} /> Delete</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatBox label="Months active" value={months} />
        <StatBox label="Current MRR" value={mrr ? formatUSD(mrr) : "—"} />
        <StatBox label="This engagement" value={formatUSD(totalPaid)} />
        {hasMultipleEngagements ? (
          <StatBox label={`Combined LTV (${relatedEngagements.length + 1} engagements)`} value={formatUSD(combinedLTV)} accent="text-brand-blue" />
        ) : (
          <StatBox
            label="Remaining"
            value={formatUSD(remaining)}
            accent={remaining > 0 ? "text-amber-600" : ""}
          />
        )}
        <StatBox label="Payments made" value={paidCount} />
        <StatBox label="Outstanding AR" value={formatUSD(outstanding)} accent={outstanding > 0 ? "text-amber-600" : ""} />
      </div>

      {hasMultipleEngagements && (
        <Card title={`Other engagements with ${client.email}`}>
          <Table>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Offer</TH>
                <TH>Status</TH>
                <TH>Started</TH>
                <TH className="text-right">Lifetime paid</TH>
                <TH></TH>
              </TR>
            </THead>
            <tbody>
              {relatedEngagements.map((r) => {
                const rOffer = offers.find((o) => o.id === r.primaryOfferId);
                const rLTV = calculateClientLTV(r.id, payments);
                return (
                  <TR key={r.id} onClick={() => { onBack(); setTimeout(() => {}, 0); }}>
                    <TD className="font-medium">{r.name}</TD>
                    <TD>{rOffer?.name || "—"}</TD>
                    <TD><Pill status={r.status} /></TD>
                    <TD>{formatDate(r.startDate)}</TD>
                    <TD className="text-right">{formatUSD(rLTV)}</TD>
                    <TD className="text-xs text-brand-slate">Go to clients list to open</TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
        </Card>
      )}

      <DealsSection
        client={client}
        payments={payments}
        offers={offers}
        dashboard={dashboard}
      />

      <Card title="Payment matrix" action={<span className="text-xs text-brand-slate">{settings.matrixMonthsAhead} months · click cell to view</span>}>
        <PaymentMatrix
          clientId={client.id}
          payments={payments}
          offers={offers}
          monthsAhead={settings.matrixMonthsAhead}
          onUpdatePayment={dashboard.updatePayment}
        />
      </Card>

      <Card title="All payments">
        {clientPayments.length === 0 ? (
          <EmptyState title="No payments yet" message="Add a payment from the Payments tab." />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Date</TH>
                <TH>Offer</TH>
                <TH>Amount</TH>
                <TH>Status</TH>
                <TH>Note</TH>
              </TR>
            </THead>
            <tbody>
              {[...clientPayments]
                .sort((a, b) => (a.paidDate || a.dueDate || a.bookedDate).localeCompare(b.paidDate || b.dueDate || b.bookedDate))
                .map((p) => {
                  const isReadonly = !!p._readonly;
                  return (
                    <TR key={p.id}>
                      <TD>{formatDate(p.paidDate || p.dueDate)}</TD>
                      <TD>
                        {isReadonly ? (
                          <span className="text-brand-slate text-xs">{offers.find((x) => x.id === p.offerId)?.name || p._ghlLineItem || "—"}</span>
                        ) : (
                          <InlinePicker
                            value={p.offerId || ""}
                            options={[{ value: "", label: "— No offer —" }, ...offers.map((o) => ({ value: o.id, label: o.name }))]}
                            onChange={(v) => dashboard.updatePayment(p.id, { offerId: v || null })}
                            tone="muted"
                            placeholder="— No offer —"
                          />
                        )}
                      </TD>
                      <TD>{formatUSD(p.amount)}</TD>
                      <TD><Pill status={p.status} /></TD>
                      <TD className="text-brand-slate">{p.note}</TD>
                    </TR>
                  );
                })}
            </tbody>
          </Table>
        )}
      </Card>

      <AddClientForm
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSubmit={(data) => dashboard.updateClient(client.id, data)}
        offers={offers}
        settings={state.settings}
        onCreateOffer={dashboard.addOffer}
        onCreateScheduledPayments={dashboard.addPaymentsBulk}
        initial={client}
        existingPayments={clientPayments}
      />
      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        onConfirm={confirm?.onConfirm || (() => {})}
      />
      <Modal
        open={!!ending}
        onClose={() => setEnding(null)}
        title={ending?.targetStatus === "completed" ? "Mark as completed" : "Mark as cancelled"}
        size="sm"
      >
        {ending && (
          <div className="space-y-3">
            <p className="text-sm text-brand-slate">
              {ending.targetStatus === "completed"
                ? "Pick the date the project ended. Used for lifetime, not counted in churn."
                : "Pick the actual cancellation date — counts as churn in this month."}
            </p>
            <Input
              label="End date *"
              type="date"
              value={ending.date}
              onChange={(e) => setEnding((s) => ({ ...s, date: e.target.value }))}
            />
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="secondary" onClick={() => setEnding(null)}>Cancel</Button>
              <Button
                variant={ending.targetStatus === "completed" ? "primary" : "danger"}
                onClick={() => {
                  dashboard.updateClient(client.id, { status: ending.targetStatus, churnDate: ending.date });
                  setEnding(null);
                }}
              >
                {ending.targetStatus === "completed" ? "Mark completed" : "Mark cancelled"}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function StatBox({ label, value, accent = "" }) {
  return (
    <div className="bg-white dark:bg-[var(--bg-surface)] rounded-lg border border-gray-100 dark:border-[var(--border-soft)] p-3">
      <div className="text-[10px] uppercase tracking-wide text-brand-slate">{label}</div>
      <div className={`mt-0.5 font-display font-semibold text-base text-brand-ink dark:text-[var(--text-primary)] ${accent}`}>{value}</div>
    </div>
  );
}
